import { Injectable } from '@angular/core';
import { WidgetService } from '../services/widget.service';
import { MediaModel, MediaFilesUpdate, MediaUpdateType, MediaCategory, MediaCollection } from './media-library.model';
import { BehaviorSubject } from 'rxjs';
import { AccountFileResponse } from '../../campaign/widgets.model';
import { DomSanitizer } from '@angular/platform-browser';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MediaLibraryService {
  private mediaFiles: MediaModel[] = [];
  public mediaFilesBS = new BehaviorSubject<MediaFilesUpdate | null>(null);
  private photoCollections: MediaCollection[] = [];
  private graphicsCollections: MediaCollection[] = [];
  private videosCollections: MediaCollection[] = [];
  public collectionsBS = new BehaviorSubject<{ [x: string]: Array<MediaCollection> } | null>(null);

  triggerbeeImagesPath: string = 'triggerbee/images/';
  triggerbeeVideosPath: string = 'triggerbee/videos/';
  imagesCategory: string = '/images/';
  videoCategory: string = '/videos/';

  constructor(
    private widgetService: WidgetService,
    private sanitizer: DomSanitizer,
  ) {}

  public getMediaFiles(): void {
    this.widgetService
      .getLoadedMediaFiles()
      .pipe(take(1))
      .subscribe((mediaFiles) => {
        this.mediaFiles = [];
        this.photoCollections = [];
        this.graphicsCollections = [];
        this.videosCollections = [];
        this.initializeMedia(mediaFiles);
        this.updateCollections();
        this.mediaFilesBS.next({ mediaFiles: this.mediaFiles, reason: MediaUpdateType.FirstFetch } as MediaFilesUpdate);
      });
  }

  private initializeMedia(media: AccountFileResponse[]): void {
    this.mediaFiles = media.map((m) => this.mapToMediaModel(m));
  }

  private mapToMediaModel(media: AccountFileResponse): MediaModel {
    const category = this.findCategory(media.uri);

    return {
      filename: media.uri.substring(media.uri.lastIndexOf('/') + 1),
      category: this.findCategory(media.uri),
      uri: media.uri,
      url: media.uri,
      style: this.sanitizer.bypassSecurityTrustStyle(`url(${media.uri})`),
      loading: true,
      collection: this.extractCollectionName(media.uri, category),
      triggerbeeImage: media.uri.includes(this.triggerbeeImagesPath),
      lastModified: media.lastModified,
    } as MediaModel;
  }

  private findCategory(mediaUrl: string): MediaCategory {
    if (mediaUrl.includes(this.videoCategory)) {
      return MediaCategory.Videos;
    }

    const categoryPath = mediaUrl.split('images/')[1];
    const category = categoryPath.split('/')[0];
    if (Object.values(MediaCategory).includes(category as MediaCategory)) {
      return category as MediaCategory;
    } else {
      return MediaCategory.Photos;
    }
  }

  private extractCollectionName(url: string, category: MediaCategory): MediaCollection | null {
    try {
      const delimiter = url.includes(this.videoCategory) ? this.videoCategory : this.imagesCategory;

      if (!url.includes(delimiter)) return null;

      const pathAfterDelimiter = url.split(delimiter)[1];
      if (!pathAfterDelimiter) return null;

      const segments = pathAfterDelimiter.split('/');
      let collectionName: string;

      if (segments[0] === category && segments.length >= 3) {
        collectionName = segments[1];
      } else if (segments[0] !== category && segments.length >= 2) {
        collectionName = segments[0];
      }

      if (collectionName) {
        const isTriggerbee = url.includes(this.triggerbeeImagesPath) || url.includes(this.triggerbeeVideosPath);
        const collection: MediaCollection = {
          name: isTriggerbee ? `Triggerbee ${collectionName.replace(/_/g, ' ')}` : collectionName,
          isTriggerbee: isTriggerbee,
        };
        this.ensureCollectionExists(collection, category);
        return collection;
      }
      return null;
    } catch {
      return null;
    }
  }

  private ensureCollectionExists(collection: MediaCollection, category: MediaCategory): void {
    const collectionsMap = {
      [MediaCategory.Photos]: this.photoCollections,
      [MediaCategory.Graphics]: this.graphicsCollections,
      [MediaCategory.Videos]: this.videosCollections,
    };
    const targetArray = collectionsMap[category];
    if (targetArray && !targetArray.some((x) => x.name === collection.name)) {
      targetArray.push(collection);
    }
  }

  private updateCollections() {
    this.collectionsBS.next({
      photos: this.photoCollections,
      graphics: this.graphicsCollections,
      videos: this.videosCollections,
    });
  }

  public addCollection(collection: MediaCollection, category: MediaCategory): void {
    if (category === MediaCategory.Photos) {
      this.photoCollections.push(collection);
      this.updateCollections();
    } else if (category === MediaCategory.Graphics) {
      this.graphicsCollections.push(collection);
      this.updateCollections();
    } else if (category === MediaCategory.Videos) {
      this.videosCollections.push(collection);
      this.updateCollections();
    }
  }

  // Renames a user folder (collection). Files in the folder are re-homed separately via
  // moveMediaFiles; this only relabels the cached collection so its folder card updates.
  public renameCollection(category: MediaCategory, oldName: string, newName: string): void {
    const collectionsMap = {
      [MediaCategory.Photos]: this.photoCollections,
      [MediaCategory.Graphics]: this.graphicsCollections,
      [MediaCategory.Icons]: [],
      [MediaCategory.Videos]: this.videosCollections,
    };
    const targetArray = collectionsMap[category];
    const entry = targetArray?.find((c) => c.name === oldName && !c.isTriggerbee);
    if (!entry) {
      return;
    }
    entry.name = newName;
    this.updateCollections();
  }

  public addMedia(mediaFile: AccountFileResponse): void {
    const newModel = this.mapToMediaModel(mediaFile);
    this.mediaFiles = [newModel, ...this.mediaFiles];
    this.mediaFilesBS.next({ mediaFiles: this.mediaFiles, reason: MediaUpdateType.Add } as MediaFilesUpdate);
    this.updateCollections();
  }

  public addMediaFiles(mediaFiles: AccountFileResponse[]): void {
    const newModels = mediaFiles.map((m) => this.mapToMediaModel(m));
    this.mediaFiles = [...newModels, ...this.mediaFiles];
    this.mediaFilesBS.next({ mediaFiles: this.mediaFiles, reason: MediaUpdateType.Add } as MediaFilesUpdate);
    this.updateCollections();
  }

  public moveMediaFiles(
    baseUrl: string,
    mediaFileNames: string[],
    destination: string,
    category?: MediaCategory,
    collection?: MediaCollection,
  ) {
    const filesToMove = this.mediaFiles.filter((mediaFile) => mediaFileNames.includes(mediaFile.filename));

    filesToMove.forEach((file) => {
      file.uri = destination ? `${baseUrl}${destination}/${file.filename}` : `${baseUrl}${file.filename}`;
      file.url = file.uri;
      file.category = category;
      file.collection = collection || null;
      file.style = this.sanitizer.bypassSecurityTrustStyle(`url(${file.url})`);
    });
    this.mediaFilesBS.next({ mediaFiles: this.mediaFiles, reason: MediaUpdateType.Update } as MediaFilesUpdate);
    this.updateCollections();
  }

  public removeMediaFiles(mediaFileNames: string[]) {
    const filenamesToRemove = new Set(mediaFileNames);
    this.mediaFiles = this.mediaFiles.filter((file) => !filenamesToRemove.has(file.filename));
    this.mediaFilesBS.next({ mediaFiles: this.mediaFiles, reason: MediaUpdateType.Remove } as MediaFilesUpdate);
    this.updateCollections();
  }
}
